import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash, 
  Search, 
  Save, 
  X, 
  CheckCircle,
  Loader2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { saveExamData, deleteExamData } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import usePaginatedData from '../../hooks/usePaginatedData';
import useDataCache from '../../hooks/useDataCache';

const ExamManagementOptimized = () => {
  const { userProfile } = useRealtime();
  const cache = useDataCache(5 * 60 * 1000); // 5-minute cache
  
  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);
  
  // OPTIMIZATION: Use paginated data hook
  // For examData, we don't use ordering since old exams may not have timestamp fields
  const { 
    data: exams, 
    loading, 
    hasMore, 
    loadMore, 
    refresh 
  } = usePaginatedData(
    'examData',
    coachingId ? { coachingId } : {},
    100, // Larger page size since exams are typically small in number
    null, // No ordering to avoid issues with missing fields
    null
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [deletingExam, setDeletingExam] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    fullName: '',
    description: '',
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    icon: '📚',
    category: 'general'
  });

  const categories = [
    { id: 'management', name: 'Management' },
    { id: 'engineering', name: 'Engineering' },
    { id: 'medical', name: 'Medical' },
    { id: 'civil_services', name: 'Civil Services' },
    { id: 'banking', name: 'Banking' },
    { id: 'government', name: 'Government' },
    { id: 'railway', name: 'Railway' },
    { id: 'teaching', name: 'Teaching' },
    { id: 'academic', name: 'Academic' },
    { id: 'law', name: 'Law' },
    { id: 'defence', name: 'Defence' },
    { id: 'general', name: 'General' }
  ];

  const colorOptions = [
    { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200' },
    { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-200' },
    { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-200' },
    { bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200' },
    { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200' },
    { bg: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200' },
    { bg: 'bg-teal-500', text: 'text-teal-600', border: 'border-teal-200' },
    { bg: 'bg-cyan-500', text: 'text-cyan-600', border: 'border-cyan-200' }
  ];

  // OPTIMIZATION: Memoize filtered exams
  const filteredExams = useMemo(() => {
    if (!searchTerm) return exams;
    
    const term = searchTerm.toLowerCase();
    return exams.filter(exam => 
      exam.name?.toLowerCase().includes(term) ||
      exam.fullName?.toLowerCase().includes(term) ||
      exam.description?.toLowerCase().includes(term) ||
      exam.category?.toLowerCase().includes(term)
    );
  }, [exams, searchTerm]);

  const handleSaveExam = async () => {
    try {
      if (!formData.id || !formData.name) {
        setError('Exam ID and Name are required');
        return;
      }

      const now = new Date();
      const examData = {
        ...formData,
        coachingId: coachingId || null,
        updatedAt: now,
        // Add createdAt only for new exams
        ...(!editingExam && { createdAt: now })
      };

      const result = await saveExamData(formData.id, examData, coachingId);
      
      if (result.success) {
        setSuccess('Exam saved successfully');
        setShowAddModal(false);
        setEditingExam(null);
        resetForm();
        refresh(); // Refresh the list
        
        // Clear cache
        cache.clear();
      } else {
        setError(result.error || 'Failed to save exam');
      }
    } catch (error) {
      setError('Failed to save exam');
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) {
      return;
    }

    try {
      setDeletingExam(examId);
      const result = await deleteExamData(examId, coachingId);
      
      if (result.success) {
        setSuccess('Exam deleted successfully');
        refresh();
        cache.clear();
      } else {
        setError(result.error || 'Failed to delete exam');
      }
    } catch (error) {
      setError('Failed to delete exam');
    } finally {
      setDeletingExam(null);
    }
  };

  const handleEditExam = (exam) => {
    setFormData(exam);
    setEditingExam(exam);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      fullName: '',
      description: '',
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      icon: '📚',
      category: 'general'
    });
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Exam Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage exams and their configurations
            {!isSuperAdmin && coachingId && (
              <span className="ml-2 text-sm text-blue-600 dark:text-blue-400">
                (Coaching Admin View)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={refresh}
            disabled={loading}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingExam(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Exam</span>
          </button>
        </div>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-2"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2"
          >
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-green-800 dark:text-green-200">{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search exams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
          />
        </div>
      </div>

      {/* Exams Grid */}
      {loading && exams.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredExams.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm ? 'No exams match your search' : 'No exams found. Add your first exam!'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExams.map((exam) => (
              <motion.div
                key={exam.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 ${exam.color} rounded-lg flex items-center justify-center text-2xl`}>
                      {exam.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {exam.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {exam.fullName}
                      </p>
                    </div>
                  </div>
                </div>
                
                {exam.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {exam.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                    {exam.category}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditExam(exam)}
                      className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteExam(exam.id)}
                      disabled={deletingExam === exam.id}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                    >
                      {deletingExam === exam.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal - Simplified for brevity */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingExam ? 'Edit Exam' : 'Add New Exam'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingExam(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Exam ID *
                </label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  disabled={!!editingExam}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  placeholder="e.g., jee, neet, cat"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Exam Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., JEE"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Joint Entrance Examination"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Brief description of the exam"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingExam(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExam}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Exam</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamManagementOptimized;
